/**
 * Seed data for B1.4 WeeklyCalendar development / demo.
 *
 * Deterministic — anchored to "this Monday 00:00 local" so a fresh dev
 * environment always shows a populated current week. Mock-only; production
 * data comes from the pet-servis booking service (ADR-B104).
 */
import type {
  AppointmentRecord,
  CustomerRecord,
  PetRecord,
  ServiceRecord,
  StaffRecord,
} from './types';
import { getMondayOfWeek } from '@/shared/ui/calendar/utils';

function iso(base: Date, dayOffset: number, h: number, m = 0): string {
  const d = new Date(base);
  d.setDate(d.getDate() + dayOffset);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

const now = new Date();
const monday = getMondayOfWeek(now);
const createdAt = new Date().toISOString();

function offsetIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(12, 0, 0, 0);
  return d.toISOString();
}

export const SEED_CUSTOMERS: CustomerRecord[] = [
  {
    id: 'cust-001',
    fullName: 'Selin Öztürk',
    phone: '+90 555 111 2233',
    email: 'selin.ozturk@example.com',
    isVip: true,
    createdAt,
    avatarUrl: 'https://i.pravatar.cc/240?img=47',
    loyaltyPoints: 2840,
    loyaltyTier: 'platinum',
    totalSpend: 18650,
    visitCount: 34,
    firstVisitAt: offsetIso(-420),
    lastVisitAt: offsetIso(-3),
    tags: ['VIP', 'Hassas cilt', 'Erken gelir'],
    internalNotes: 'Hipoalerjenik şampuan. Sakinleştirici ortam tercih ediyor.',
    preferredStaffId: 'staff-ayse',
    preferredContactChannel: 'whatsapp',
  },
  {
    id: 'cust-002',
    fullName: 'Burak Çelik',
    phone: '+90 555 222 3344',
    email: 'burak.celik@example.com',
    isVip: false,
    createdAt,
    avatarUrl: 'https://i.pravatar.cc/240?img=12',
    loyaltyPoints: 640,
    loyaltyTier: 'silver',
    totalSpend: 3200,
    visitCount: 8,
    firstVisitAt: offsetIso(-180),
    lastVisitAt: offsetIso(-12),
    tags: ['Geç kalıyor'],
    preferredContactChannel: 'sms',
  },
  {
    id: 'cust-003',
    fullName: 'Ece Polat',
    phone: '+90 555 333 4455',
    email: 'ece.polat@example.com',
    isVip: false,
    createdAt,
    avatarUrl: 'https://i.pravatar.cc/240?img=32',
    loyaltyPoints: 260,
    loyaltyTier: 'bronze',
    totalSpend: 1450,
    visitCount: 4,
    firstVisitAt: offsetIso(-90),
    lastVisitAt: offsetIso(-7),
    preferredContactChannel: 'whatsapp',
  },
  {
    id: 'cust-004',
    fullName: 'Kerem Yalçın',
    phone: '+90 555 444 5566',
    email: 'kerem.yalcin@example.com',
    isVip: false,
    createdAt,
    avatarUrl: 'https://i.pravatar.cc/240?img=15',
    loyaltyPoints: 980,
    loyaltyTier: 'silver',
    totalSpend: 5400,
    visitCount: 11,
    firstVisitAt: offsetIso(-240),
    lastVisitAt: offsetIso(-5),
    tags: ['Eğitim odaklı'],
    preferredStaffId: 'staff-emre',
    preferredContactChannel: 'call',
  },
  {
    id: 'cust-005',
    fullName: 'Deniz Acar',
    phone: '+90 555 555 6677',
    isVip: false,
    createdAt,
    avatarUrl: 'https://i.pravatar.cc/240?img=23',
    loyaltyPoints: 420,
    loyaltyTier: 'bronze',
    totalSpend: 1950,
    visitCount: 6,
    firstVisitAt: offsetIso(-120),
    lastVisitAt: offsetIso(-2),
    preferredContactChannel: 'sms',
  },
  {
    id: 'cust-006',
    fullName: 'Gül Aydın',
    phone: '+90 555 666 7788',
    email: 'gul.aydin@example.com',
    isVip: true,
    createdAt,
    avatarUrl: 'https://i.pravatar.cc/240?img=41',
    loyaltyPoints: 1920,
    loyaltyTier: 'gold',
    totalSpend: 11200,
    visitCount: 22,
    firstVisitAt: offsetIso(-365),
    lastVisitAt: offsetIso(-1),
    tags: ['VIP', 'Veteriner bakım'],
    internalNotes: 'Yıllık check-up döngüsünde. Kan tahlili tercih ediyor.',
    preferredStaffId: 'staff-zeynep',
    preferredContactChannel: 'email',
  },
  {
    id: 'cust-007',
    fullName: 'Can Bilir',
    phone: '+90 555 777 8899',
    isVip: false,
    createdAt,
    avatarUrl: 'https://i.pravatar.cc/240?img=8',
    loyaltyPoints: 180,
    loyaltyTier: 'bronze',
    totalSpend: 780,
    visitCount: 3,
    firstVisitAt: offsetIso(-60),
    lastVisitAt: offsetIso(-20),
    preferredContactChannel: 'sms',
  },
  {
    id: 'cust-008',
    fullName: 'Elif Duran',
    phone: '+90 555 888 9900',
    email: 'elif.duran@example.com',
    isVip: false,
    createdAt,
    avatarUrl: 'https://i.pravatar.cc/240?img=38',
    loyaltyPoints: 540,
    loyaltyTier: 'silver',
    totalSpend: 2800,
    visitCount: 7,
    firstVisitAt: offsetIso(-150),
    lastVisitAt: offsetIso(-10),
    preferredContactChannel: 'whatsapp',
  },
  {
    id: 'cust-009',
    fullName: 'Ozan Keskin',
    phone: '+90 555 999 0011',
    isVip: false,
    createdAt,
    avatarUrl: 'https://i.pravatar.cc/240?img=14',
    loyaltyPoints: 720,
    loyaltyTier: 'silver',
    totalSpend: 4100,
    visitCount: 9,
    firstVisitAt: offsetIso(-200),
    lastVisitAt: offsetIso(-4),
    tags: ['Grup eğitim'],
    preferredStaffId: 'staff-emre',
    preferredContactChannel: 'call',
  },
  {
    id: 'cust-010',
    fullName: 'Nazlı Çınar',
    phone: '+90 555 123 4567',
    email: 'nazli.cinar@example.com',
    isVip: true,
    createdAt,
    avatarUrl: 'https://i.pravatar.cc/240?img=45',
    loyaltyPoints: 2210,
    loyaltyTier: 'gold',
    totalSpend: 13800,
    visitCount: 26,
    firstVisitAt: offsetIso(-540),
    lastVisitAt: offsetIso(-6),
    tags: ['VIP', 'Spa bağımlısı'],
    internalNotes: 'Pamuk için premium spa tercih ediyor. Her ay düzenli.',
    preferredStaffId: 'staff-ayse',
    preferredContactChannel: 'whatsapp',
  },
];

export const SEED_PETS: PetRecord[] = [
  {
    id: 'pet-001',
    customerId: 'cust-001',
    name: 'Luna',
    species: 'dog',
    breed: 'Golden Retriever',
    createdAt,
    photoUrl: 'https://images.unsplash.com/photo-1552053831-71594a27632d?w=480&auto=format',
    birthDate: offsetIso(-365 * 4),
    sex: 'female',
    neutered: true,
    weightKg: 28.4,
    allergies: ['Tavuk proteini'],
    chronicConditions: ['Atopik dermatit'],
    behaviorNotes: 'Tırnak kesiminde stres. Yavaş yaklaşım + ödül işe yarıyor.',
    microchipId: 'TR-900-111-2233',
    vaccinations: [
      { id: 'vac-1', name: 'Karma', appliedAt: offsetIso(-300), nextDueAt: offsetIso(65), status: 'current' },
      { id: 'vac-2', name: 'Kuduz', appliedAt: offsetIso(-330), nextDueAt: offsetIso(35), status: 'due_soon' },
    ],
  },
  {
    id: 'pet-002',
    customerId: 'cust-002',
    name: 'Max',
    species: 'dog',
    breed: 'Labrador',
    createdAt,
    photoUrl: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=480&auto=format',
    birthDate: offsetIso(-365 * 2),
    sex: 'male',
    neutered: false,
    weightKg: 32.1,
    behaviorNotes: 'Aşırı enerjik, tasma kontrolü önemli.',
    vaccinations: [
      { id: 'vac-3', name: 'Karma', appliedAt: offsetIso(-200), nextDueAt: offsetIso(165), status: 'current' },
      { id: 'vac-4', name: 'Kuduz', appliedAt: offsetIso(-220), nextDueAt: offsetIso(145), status: 'current' },
    ],
  },
  {
    id: 'pet-003',
    customerId: 'cust-003',
    name: 'Mia',
    species: 'cat',
    breed: 'British Shorthair',
    createdAt,
    photoUrl: 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=480&auto=format',
    birthDate: offsetIso(-365 * 3),
    sex: 'female',
    neutered: true,
    weightKg: 4.2,
    allergies: ['Balık unu'],
    vaccinations: [
      { id: 'vac-5', name: 'Karma', appliedAt: offsetIso(-410), nextDueAt: offsetIso(-45), status: 'overdue' },
    ],
  },
  {
    id: 'pet-004',
    customerId: 'cust-004',
    name: 'Rocky',
    species: 'dog',
    breed: 'Alman Çoban',
    createdAt,
    photoUrl: 'https://images.unsplash.com/photo-1589941013453-ec89f33b5e95?w=480&auto=format',
    birthDate: offsetIso(-365 * 5),
    sex: 'male',
    neutered: true,
    weightKg: 36.8,
    behaviorNotes: 'İtaat eğitimi 2. seviyede. Yabancılara temkinli.',
    vaccinations: [
      { id: 'vac-6', name: 'Karma', appliedAt: offsetIso(-110), nextDueAt: offsetIso(255), status: 'current' },
    ],
  },
  {
    id: 'pet-005',
    customerId: 'cust-005',
    name: 'Bella',
    species: 'dog',
    breed: 'Pomeranian',
    createdAt,
    photoUrl: 'https://images.unsplash.com/photo-1633722715463-d30f4f325e24?w=480&auto=format',
    birthDate: offsetIso(-365),
    sex: 'female',
    neutered: false,
    weightKg: 3.1,
    behaviorNotes: 'Kurutma fonuna hassas. Düşük ısı kullanın.',
    vaccinations: [
      { id: 'vac-7', name: 'Karma', appliedAt: offsetIso(-30), nextDueAt: offsetIso(335), status: 'current' },
    ],
  },
  {
    id: 'pet-006',
    customerId: 'cust-006',
    name: 'Oscar',
    species: 'cat',
    breed: 'Scottish Fold',
    createdAt,
    photoUrl: 'https://images.unsplash.com/photo-1574158622682-e40e69881006?w=480&auto=format',
    birthDate: offsetIso(-365 * 6),
    sex: 'male',
    neutered: true,
    weightKg: 5.4,
    chronicConditions: ['Eklem rahatsızlığı'],
    vaccinations: [
      { id: 'vac-8', name: 'Karma', appliedAt: offsetIso(-90), nextDueAt: offsetIso(275), status: 'current' },
      { id: 'vac-9', name: 'Kuduz', appliedAt: offsetIso(-90), nextDueAt: offsetIso(275), status: 'current' },
    ],
  },
  {
    id: 'pet-007',
    customerId: 'cust-007',
    name: 'Charlie',
    species: 'dog',
    breed: 'Beagle',
    createdAt,
    photoUrl: 'https://images.unsplash.com/photo-1505628346881-b72b27e84530?w=480&auto=format',
    birthDate: offsetIso(-365 * 3),
    sex: 'male',
    neutered: false,
    weightKg: 14.2,
    behaviorNotes: 'Koku takibine yatkın, uzun kayış önerilir.',
    vaccinations: [
      { id: 'vac-10', name: 'Karma', appliedAt: offsetIso(-150), nextDueAt: offsetIso(215), status: 'current' },
    ],
  },
  {
    id: 'pet-008',
    customerId: 'cust-008',
    name: 'Duman',
    species: 'cat',
    breed: 'Tekir',
    createdAt,
    photoUrl: 'https://images.unsplash.com/photo-1543852786-1cf6624b9987?w=480&auto=format',
    birthDate: offsetIso(-365 * 2),
    sex: 'male',
    neutered: true,
    weightKg: 4.8,
    vaccinations: [
      { id: 'vac-11', name: 'Karma', appliedAt: offsetIso(-60), nextDueAt: offsetIso(305), status: 'current' },
    ],
  },
  {
    id: 'pet-009',
    customerId: 'cust-009',
    name: 'Zeus',
    species: 'dog',
    breed: 'Rottweiler',
    createdAt,
    photoUrl: 'https://images.unsplash.com/photo-1567014543648-e4391c989aab?w=480&auto=format',
    birthDate: offsetIso(-365 * 4),
    sex: 'male',
    neutered: true,
    weightKg: 45.6,
    behaviorNotes: 'Grup eğitiminde lider pozisyon. Sabırlı yaklaşım ister.',
    vaccinations: [
      { id: 'vac-12', name: 'Karma', appliedAt: offsetIso(-180), nextDueAt: offsetIso(185), status: 'current' },
    ],
  },
  {
    id: 'pet-010',
    customerId: 'cust-010',
    name: 'Pamuk',
    species: 'dog',
    breed: 'Maltese',
    createdAt,
    photoUrl: 'https://images.unsplash.com/photo-1583512603805-3cc6b41f3edb?w=480&auto=format',
    birthDate: offsetIso(-365 * 2),
    sex: 'female',
    neutered: true,
    weightKg: 3.4,
    allergies: ['Parfüm'],
    behaviorNotes: 'Premium spa favorisi. Yumuşatıcı kür uygulanıyor.',
    vaccinations: [
      { id: 'vac-13', name: 'Karma', appliedAt: offsetIso(-20), nextDueAt: offsetIso(345), status: 'current' },
      { id: 'vac-14', name: 'Kuduz', appliedAt: offsetIso(-20), nextDueAt: offsetIso(345), status: 'current' },
    ],
  },
];

export const SEED_SERVICES: ServiceRecord[] = [
  { id: 'svc-groom-full', name: 'Tam Tıraş + Tırnak', category: 'grooming', durationMin: 90, price: 650, currency: 'TRY', qualifiedStaffIds: ['staff-ayse', 'staff-mehmet'], createdAt },
  { id: 'svc-groom-bath', name: 'Banyo + Kurutma', category: 'grooming', durationMin: 60, price: 350, currency: 'TRY', qualifiedStaffIds: ['staff-ayse', 'staff-mehmet'], createdAt },
  { id: 'svc-groom-nails', name: 'Tırnak Kesimi', category: 'grooming', durationMin: 30, price: 120, currency: 'TRY', qualifiedStaffIds: ['staff-ayse', 'staff-mehmet'], createdAt },
  { id: 'svc-groom-spa', name: 'Premium Spa', category: 'grooming', durationMin: 120, price: 1200, currency: 'TRY', qualifiedStaffIds: ['staff-ayse'], createdAt },
  { id: 'svc-vet-checkup', name: 'Yıllık Check-up', category: 'vet', durationMin: 60, price: 750, currency: 'TRY', qualifiedStaffIds: ['staff-zeynep'], createdAt },
  { id: 'svc-vet-vaccine', name: 'Aşı Kontrolü', category: 'vet', durationMin: 30, price: 400, currency: 'TRY', qualifiedStaffIds: ['staff-zeynep'], createdAt },
  { id: 'svc-vet-flea', name: 'Pire / Kene Tedavisi', category: 'vet', durationMin: 30, price: 280, currency: 'TRY', qualifiedStaffIds: ['staff-zeynep'], createdAt },
  { id: 'svc-train-obed', name: 'İtaat Eğitimi', category: 'training', durationMin: 60, price: 500, currency: 'TRY', qualifiedStaffIds: ['staff-emre'], createdAt },
  { id: 'svc-train-group', name: 'Grup Eğitimi', category: 'training', durationMin: 90, price: 600, currency: 'TRY', qualifiedStaffIds: ['staff-emre'], createdAt },
];

export const SEED_STAFF: StaffRecord[] = [
  {
    id: 'staff-ayse',
    fullName: 'Ayşe Kaya',
    role: 'Baş Kuaför',
    isActive: true,
    createdAt,
  },
  {
    id: 'staff-mehmet',
    fullName: 'Mehmet Demir',
    role: 'Kuaför',
    isActive: true,
    createdAt,
  },
  {
    id: 'staff-zeynep',
    fullName: 'Dr. Zeynep Yıldız',
    role: 'Veteriner',
    isActive: true,
    createdAt,
  },
  {
    id: 'staff-emre',
    fullName: 'Emre Aslan',
    role: 'Eğitmen',
    isActive: true,
    createdAt,
  },
];

export const SEED_APPOINTMENTS: AppointmentRecord[] = [
  {
    id: 'apt-001',
    source: 'marketplace',
    externalBookingId: '11111111-1111-4111-8111-111111111111',
    customerId: 'cust-001',
    customerName: 'Selin Öztürk',
    petId: 'pet-001',
    petName: 'Luna',
    serviceName: 'Tam Tıraş + Tırnak',
    serviceCategory: 'grooming',
    staffId: 'staff-ayse',
    status: 'confirmed',
    startTime: iso(monday, 0, 10, 0),
    endTime: iso(monday, 0, 11, 30),
    isVip: true,
    price: 650,
    currency: 'TRY',
    notes: 'Hassas cilt — hipoalerjenik şampuan.',
    createdAt,
    updatedAt: createdAt,
  },
  {
    id: 'apt-002',
    customerId: 'cust-002',
    customerName: 'Burak Çelik',
    petId: 'pet-002',
    petName: 'Max',
    serviceName: 'Aşı Kontrolü',
    serviceCategory: 'vet',
    staffId: 'staff-zeynep',
    status: 'confirmed',
    startTime: iso(monday, 0, 14, 0),
    endTime: iso(monday, 0, 14, 30),
    price: 400,
    currency: 'TRY',
    createdAt,
    updatedAt: createdAt,
  },
  {
    id: 'apt-003',
    customerId: 'cust-003',
    customerName: 'Ece Polat',
    petId: 'pet-003',
    petName: 'Mia',
    serviceName: 'Tırnak Kesimi',
    serviceCategory: 'grooming',
    staffId: 'staff-mehmet',
    status: 'pending',
    startTime: iso(monday, 1, 9, 30),
    endTime: iso(monday, 1, 10, 0),
    price: 120,
    currency: 'TRY',
    createdAt,
    updatedAt: createdAt,
  },
  {
    id: 'apt-004',
    customerId: 'cust-004',
    customerName: 'Kerem Yalçın',
    petId: 'pet-004',
    petName: 'Rocky',
    serviceName: 'İtaat Eğitimi',
    serviceCategory: 'training',
    staffId: 'staff-emre',
    status: 'confirmed',
    startTime: iso(monday, 1, 11, 0),
    endTime: iso(monday, 1, 12, 0),
    price: 500,
    currency: 'TRY',
    createdAt,
    updatedAt: createdAt,
  },
  {
    id: 'apt-005',
    customerId: 'cust-005',
    customerName: 'Deniz Acar',
    petId: 'pet-005',
    petName: 'Bella',
    serviceName: 'Banyo + Kurutma',
    serviceCategory: 'grooming',
    staffId: 'staff-ayse',
    status: 'in_progress',
    startTime: iso(monday, 2, 10, 0),
    endTime: iso(monday, 2, 11, 0),
    price: 350,
    currency: 'TRY',
    createdAt,
    updatedAt: createdAt,
  },
  {
    id: 'apt-006',
    customerId: 'cust-006',
    customerName: 'Gül Aydın',
    petId: 'pet-006',
    petName: 'Oscar',
    serviceName: 'Yıllık Check-up',
    serviceCategory: 'vet',
    staffId: 'staff-zeynep',
    status: 'confirmed',
    startTime: iso(monday, 2, 15, 0),
    endTime: iso(monday, 2, 16, 0),
    isVip: true,
    price: 750,
    currency: 'TRY',
    notes: 'Kan tahlili dahil.',
    createdAt,
    updatedAt: createdAt,
  },
  {
    id: 'apt-007',
    customerId: 'cust-007',
    customerName: 'Can Bilir',
    petId: 'pet-007',
    petName: 'Charlie',
    serviceName: 'Pire / Kene Tedavisi',
    serviceCategory: 'vet',
    staffId: 'staff-zeynep',
    status: 'confirmed',
    startTime: iso(monday, 3, 10, 30),
    endTime: iso(monday, 3, 11, 0),
    price: 280,
    currency: 'TRY',
    createdAt,
    updatedAt: createdAt,
  },
  {
    id: 'apt-008',
    customerId: 'cust-008',
    customerName: 'Elif Duran',
    petId: 'pet-008',
    petName: 'Duman',
    serviceName: 'Kısa Tıraş',
    serviceCategory: 'grooming',
    staffId: 'staff-mehmet',
    status: 'confirmed',
    startTime: iso(monday, 3, 13, 0),
    endTime: iso(monday, 3, 14, 0),
    price: 450,
    currency: 'TRY',
    createdAt,
    updatedAt: createdAt,
  },
  {
    id: 'apt-009',
    customerId: 'cust-009',
    customerName: 'Ozan Keskin',
    petId: 'pet-009',
    petName: 'Zeus',
    serviceName: 'Grup Eğitimi',
    serviceCategory: 'training',
    staffId: 'staff-emre',
    status: 'confirmed',
    startTime: iso(monday, 4, 10, 0),
    endTime: iso(monday, 4, 11, 30),
    price: 600,
    currency: 'TRY',
    createdAt,
    updatedAt: createdAt,
  },
  {
    id: 'apt-010',
    customerId: 'cust-010',
    customerName: 'Nazlı Çınar',
    petId: 'pet-010',
    petName: 'Pamuk',
    serviceName: 'Premium Spa',
    serviceCategory: 'grooming',
    staffId: 'staff-ayse',
    status: 'pending',
    startTime: iso(monday, 4, 14, 0),
    endTime: iso(monday, 4, 16, 0),
    isVip: true,
    price: 1200,
    currency: 'TRY',
    notes: 'Yumuşatıcı kür + tırnak kesimi dahil.',
    createdAt,
    updatedAt: createdAt,
  },
];
